import React, { useState, useEffect, useMemo } from "react";
import { map } from "lodash";
import moment, { type Moment } from "moment";
import { CprDateRangePicker } from "../../../custom-elements/cps-datepicker/cps-daterangepicker.component";
import { dateRangeTypes } from "./date-filter.helper";
import { defaultDateFormat } from "../column.helper";
import type {
  DateRangesConfig,
  FilterSelections,
} from "../cp-table-header-menu.types";

import styles from "./filter.styles.css";

type DateRangeFilterProps = {
  applyFilter: (
    selectedFilter: string | string[] | undefined,
    filter: {
      before?: Moment | string | null;
      after?: Moment | string | null;
    },
  ) => void;
  filter: FilterSelections;
  dateRanges: DateRangesConfig;
  hideSortOptions?: boolean;
  setDisableSave: (disable: boolean) => void;
};

const DateRangeFilter = ({
  applyFilter,
  filter,
  dateRanges,
  hideSortOptions,
  setDisableSave,
}: DateRangeFilterProps) => {
  const initialDateRange = useMemo(() => {
    // If filter_params are given (e.g. "today", "last_week") but there is no after or before, then initialize the start and end dates to that filter_params range
    if (filter.filter_params && !filter.after && !filter.before) {
      const filterParamsKey = Array.isArray(filter.filter_params)
        ? filter.filter_params[0]
        : filter.filter_params;
      const ranges = dateRanges[filterParamsKey]?.filter || null;
      return {
        after: ranges?.after
          ? moment(ranges.after).format(defaultDateFormat)
          : undefined,
        before: ranges?.before
          ? moment(ranges.before).format(defaultDateFormat)
          : undefined,
      };
    }
    return null;
  }, [filter, dateRanges]);

  const [startDateText, setStartDateText] = useState(initialDateRange?.after);
  const [startDateInvalid, setStartDateInvalid] = useState(false);
  const [endDateText, setEndDateText] = useState(initialDateRange?.before);
  const [endDateInvalid, setEndDateInvalid] = useState(false);

  useEffect(() => {
    setStartDateText(initialDateRange?.after || filter.after || "");
    setStartDateInvalid(false);
    setEndDateText(initialDateRange?.before || filter.before || "");
    setEndDateInvalid(false);
  }, [filter, initialDateRange]);

  const handleRangeChange = (e: {
    detail: { startDate: string; endDate: string };
  }) => {
    if (
      e.detail.startDate === startDateText &&
      e.detail.endDate === endDateText
    )
      return;
    updateDates(dateRangeTypes.custom, e.detail.startDate, e.detail.endDate);
  };

  const startDateOnBlur = (value: string) => {
    if (!value || moment(new Date(value)).isValid()) {
      updateDates(dateRangeTypes.custom, value, filter.before);
      setDisableSave(false);
    } else {
      setStartDateInvalid(true);
      setDisableSave(true);
    }
  };

  const endDateOnBlur = (value: string) => {
    if (!value || moment(new Date(value)).isValid()) {
      updateDates(dateRangeTypes.custom, filter.after, value);
      setDisableSave(false);
    } else {
      setEndDateInvalid(true);
      setDisableSave(true);
    }
  };

  const updateDates = (
    selectedDateFilter: string,
    after?: string,
    before?: string,
  ) => {
    setStartDateText(after || "");
    setStartDateInvalid(false);
    setEndDateText(before || "");
    setEndDateInvalid(false);
    applyFilter(selectedDateFilter, {
      after: after ? moment(new Date(after)) : null,
      before: before ? moment(new Date(before)) : null,
    });
  };

  const checkForValidDate = (str: string, setStr: (value: string) => void) => {
    const regex = new RegExp("^[0-9/]*$");
    if (regex.test(str)) {
      setStr(str);
      if (!str || moment(new Date(str)).isValid()) {
        setDisableSave(false);
      }
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <div>
        {!hideSortOptions && (
          <li className="cps-divider cps-margin-left-16 cps-margin-right-16 cps-margin-top-4 cps-margin-bottom-4" />
        )}
        {map(dateRanges, (dateRange, dateRangeKey) => {
          return (
            <li
              key={dateRangeKey}
              style={{ width: "200px" }}
              className="cps-cursor-pointer"
            >
              <div
                className={`${styles.spreadContents} cps-margin-left-16 cps-margin-right-16 cps-padding-top-8 cps-padding-bottom-8`}
                onClick={() => applyFilter(dateRangeKey, dateRange.filter)}
                style={{ lineHeight: "20px" }}
              >
                <div
                  className={
                    filter && filter.filter_params === dateRangeKey
                      ? "cps-color-primary cp-wt-semibold"
                      : ""
                  }
                >
                  {dateRange.label}
                </div>
                <div style={{ minWidth: "25px", margin: "-3px" }}>
                  {filter && filter.filter_params === dateRangeKey && (
                    <i className="cps-color-primary cps-icon cps-icon-sm-check" />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </div>
      <div
        className="cps-margin-left-16 cps-margin-right-16"
        style={{
          marginTop: hideSortOptions ? "0px" : "-76px",
          marginLeft: "24px",
        }}
      >
        <CprDateRangePicker
          startDate={initialDateRange?.after || filter.after}
          endDate={initialDateRange?.before || filter.before}
          events={{
            rangechange: handleRangeChange,
          }}
        />
        <div className="cps-form-group">
          <div style={{ display: "flex", whiteSpace: "nowrap" }}>
            <label
              style={{ alignSelf: "center" }}
              htmlFor="startDate"
              className="cps-margin-left-12 cps-margin-right-8"
            >
              Start date
            </label>
            <div
              className={`cps-margin-right-16 ${
                startDateInvalid ? "cps-has-error" : ""
              }`}
            >
              <input
                type="text"
                className="cps-form-control"
                value={startDateText}
                onChange={(e) =>
                  checkForValidDate(e.target.value, setStartDateText)
                }
                onBlur={(e) => startDateOnBlur(e.target.value)}
                placeholder={defaultDateFormat}
              />
              <span className="cps-error-block">Invalid date</span>
            </div>
          </div>
          <div style={{ display: "flex", whiteSpace: "nowrap" }}>
            <label
              style={{ alignSelf: "center" }}
              htmlFor="endDate"
              className="cps-margin-left-12 cps-margin-right-12"
            >
              End date
            </label>
            <div
              className={`cps-margin-right-16 ${
                endDateInvalid ? "cps-has-error" : ""
              }`}
            >
              <input
                type="text"
                className="cps-form-control cps-margin-top-8 cps-margin-bottom-8"
                value={endDateText}
                onChange={(e) =>
                  checkForValidDate(e.target.value, setEndDateText)
                }
                onBlur={(e) => endDateOnBlur(e.target.value)}
                placeholder={defaultDateFormat}
                disabled={!startDateText || startDateInvalid}
              />
              <span className="cps-error-block">Invalid date</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;
